/**
 * Golf Profiles Page
 *
 * Create and manage golfer profiles, then simulate tournaments
 * to see how skill profiles affect outcomes.
 */

import React, { useState, useCallback, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { PageTitle, H2Heading } from '../../../components/Headers';
import { PageBody } from '../../../components/PageBody';
import { ArticleParagraph } from '../../../components/Paragraphs';

import { GolferProfileEditor } from './components/GolferProfileEditor';
import { SkillRadarChartCompact } from './components/SkillRadarChart';
import {
    CourseSelector,
    CourseSummaryCard,
    TournamentConfig,
    LeaderboardView,
    TournamentStats,
    SimulationResultsTable
} from './components/TournamentComponents';

import {
    createGolferProfile,
    generateRandomGolfers,
    GOLFER_PRESETS,
    SKILL_LABELS,
    getHandicapCategory
} from './utility/golferProfiles';
import { COURSE_PRESETS, calculateCourseFit } from './utility/courseProfiles';
import {
    simulateTournament,
    aggregateSimulationResults,
} from './utility/tournamentSimulation';


/**
 * Compact player table row
 */
const PlayerRow = ({ golfer, selected, onSelect, onRemove, canRemove, courseFit }) => {
    const category = getHandicapCategory(golfer.handicapIndex);
    const fitSign = courseFit >= 0 ? '+' : '';

    return (
        <tr
            onClick={onSelect}
            className={`cursor-pointer text-sm transition-colors ${
                selected
                    ? 'bg-green-50 border-l-2 border-l-green-500'
                    : 'hover:bg-gray-50 border-l-2 border-l-transparent'
            }`}
        >
            <td className="px-3 py-2">
                <span className="font-medium text-gray-800">{golfer.name}</span>
                <span className="text-xs text-gray-400 ml-1">({golfer.nationality})</span>
            </td>
            <td className="px-3 py-2 text-center text-gray-600">{golfer.handicapIndex}</td>
            <td className="px-3 py-2 text-xs text-gray-500 hidden md:table-cell">{category}</td>
            <td className="px-2 py-1 hidden sm:table-cell">
                <div className="flex items-center gap-1">
                    <SkillRadarChartCompact
                        skills={golfer.skills}
                        handicap={golfer.handicapIndex}
                        consistency={golfer.consistency}
                        size={32}
                    />
                    <div className="flex flex-col gap-0.5">
                        {Object.entries(golfer.skills).map(([key, value]) => (
                            <div
                                key={key}
                                className="w-1.5 h-1.5 rounded-full"
                                title={`${SKILL_LABELS[key]}: ${value >= 0 ? '+' : ''}${value.toFixed(2)}`}
                                style={{
                                    backgroundColor: value >= 0.2 ? '#22C55E'
                                        : value >= -0.2 ? '#9CA3AF'
                                        : '#EF4444'
                                }}
                            />
                        ))}
                    </div>
                </div>
            </td>
            <td className="px-2 py-2 text-center hidden sm:table-cell">
                <span className={`font-mono text-xs ${courseFit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {fitSign}{courseFit.toFixed(2)}
                </span>
            </td>
            <td className="px-2 py-2 text-center">
                {canRemove && (
                    <button
                        onClick={(e) => { e.stopPropagation(); onRemove(); }}
                        className="text-gray-300 hover:text-red-500 text-sm leading-none"
                        title="Remove golfer"
                    >
                        ×
                    </button>
                )}
            </td>
        </tr>
    );
};


/**
 * Main Golf Profiles page component
 */
export const GolfProfiles = () => {
    // Golfer management state — default to one of each archetype
    const [golfers, setGolfers] = useState(() =>
        Object.values(GOLFER_PRESETS)
    );
    const [selectedGolferId, setSelectedGolferId] = useState(null);
    const [editingGolfer, setEditingGolfer] = useState(false);

    // Tournament state
    const [course, setCourse] = useState(COURSE_PRESETS.stAndrews);
    const [tournamentConfig, setTournamentConfig] = useState({
        name: 'Championship',
        rounds: 4,
        cutAfterRound: 2,
        cutRule: 'bottomPercentage',
        cutPercentage: 40,
        prestigeLevel: 'major'
    });

    // Results state
    const [singleResult, setSingleResult] = useState(null);
    const [multiResults, setMultiResults] = useState(null);
    const [isSimulating, setIsSimulating] = useState(false);
    const [simProgress, setSimProgress] = useState({ done: 0, total: 0 });
    const simCancelRef = useRef(false);

    // Get selected golfer
    const selectedGolfer = golfers.find(g => g.id === selectedGolferId);

    // Pre-compute course fit for all golfers (used in table and course card)
    const courseFitMap = useMemo(() => {
        const map = {};
        golfers.forEach(g => { map[g.id] = calculateCourseFit(g, course); });
        return map;
    }, [golfers, course]);

    // Handlers
    const handleGolferUpdate = useCallback((updatedGolfer) => {
        setGolfers(prev => prev.map(g =>
            g.id === updatedGolfer.id ? updatedGolfer : g
        ));
    }, []);

    const handleAddGolfer = useCallback(() => {
        const newGolfer = createGolferProfile({
            name: `Player ${golfers.length + 1}`
        });
        setGolfers(prev => [...prev, newGolfer]);
        setSelectedGolferId(newGolfer.id);
        setEditingGolfer(true);
    }, [golfers.length]);

    const handleAddRandomGolfers = useCallback((count) => {
        const newGolfers = generateRandomGolfers(count);
        setGolfers(prev => [...prev, ...newGolfers]);
    }, []);

    const handleRemoveGolfer = useCallback((golferId) => {
        setGolfers(prev => prev.filter(g => g.id !== golferId));
        if (selectedGolferId === golferId) {
            setSelectedGolferId(null);
            setEditingGolfer(false);
        }
    }, [selectedGolferId]);

    const handleSelectGolfer = useCallback((golferId) => {
        setSelectedGolferId(golferId);
        setEditingGolfer(true);
    }, []);

    const handleCloseEditor = useCallback(() => {
        setEditingGolfer(false);
        setSelectedGolferId(null);
    }, []);

    const handleSimulateSingle = useCallback(() => {
        if (golfers.length < 2) return;

        setIsSimulating(true);
        setSimProgress({ done: 0, total: 1 });
        setTimeout(() => {
            const result = simulateTournament(golfers, course, tournamentConfig);
            setSingleResult(result);
            setMultiResults(null);
            setIsSimulating(false);
            setSimProgress({ done: 0, total: 0 });
        }, 50);
    }, [golfers, course, tournamentConfig]);

    const handleSimulateMultiple = useCallback((count) => {
        if (golfers.length < 2) return;

        setIsSimulating(true);
        setSingleResult(null);
        setMultiResults(null);
        setSimProgress({ done: 0, total: count });
        simCancelRef.current = false;

        const BATCH = 25;
        const allResults = [];
        let completed = 0;

        const runBatch = () => {
            if (simCancelRef.current) {
                setIsSimulating(false);
                setSimProgress({ done: 0, total: 0 });
                return;
            }

            const batchSize = Math.min(BATCH, count - completed);
            for (let i = 0; i < batchSize; i++) {
                allResults.push(simulateTournament(golfers, course, tournamentConfig));
            }
            completed += batchSize;
            setSimProgress({ done: completed, total: count });

            if (completed >= count) {
                const results = aggregateSimulationResults(allResults, golfers, course, tournamentConfig);
                setMultiResults(results);
                setIsSimulating(false);
                setSimProgress({ done: 0, total: 0 });
            } else {
                setTimeout(runBatch, 0);
            }
        };

        setTimeout(runBatch, 50);
    }, [golfers, course, tournamentConfig]);

    return (
        <PageBody>
            <Helmet>
                <title>Golf Profiles & Tournament Simulation | VizBadger</title>
                <meta
                    name="description"
                    content="Create golfer profiles with skill breakdowns and simulate tournaments to see how different attributes affect outcomes."
                />
                <meta name="keywords" content="Golf simulation, golfer profiles, tournament simulation, handicap, strokes gained, golf analytics" />
            </Helmet>

            <PageTitle title="Golf Profiles & Tournament Simulation" />

            <ArticleParagraph
                lines={[
                    "Create golfer profiles with detailed skill breakdowns and simulate tournaments to understand how different attributes affect outcomes. Each golfer has a handicap (overall ability) plus skill modifiers that describe their strengths and weaknesses.",
                    "The simulation uses these profiles along with course characteristics and conditions to produce realistic tournament results. Run multiple simulations to see statistical outcomes."
                ]}
            />

            {/* Section 1: Tournament Settings (full width) */}
            <div className="bg-white border border-gray-200 rounded-lg p-4 mt-6">
                <h3 className="font-semibold text-gray-800 mb-3 text-sm">Tournament Settings</h3>
                <TournamentConfig
                    config={tournamentConfig}
                    onConfigChange={setTournamentConfig}
                    fieldSize={golfers.length}
                />
            </div>

            {/* Section 2: Players (2/3) + Right panel (1/3) */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">

                {/* Players table */}
                <div className="lg:col-span-2 order-2 lg:order-1">
                    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                        <div className="flex justify-between items-center px-4 py-3 border-b border-gray-200">
                            <h3 className="font-semibold text-gray-800 text-sm">Field ({golfers.length})</h3>
                            <div className="flex gap-2">
                                <button
                                    onClick={handleAddGolfer}
                                    className="text-xs px-2.5 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                                >
                                    + Add
                                </button>
                                <button
                                    onClick={() => handleAddRandomGolfers(4)}
                                    className="text-xs px-2.5 py-1 border border-gray-300 rounded hover:bg-gray-50"
                                >
                                    + 4 Random
                                </button>
                                <button
                                    onClick={() => handleAddRandomGolfers(8)}
                                    className="text-xs px-2.5 py-1 border border-gray-300 rounded hover:bg-gray-50"
                                >
                                    + 8 Random
                                </button>
                            </div>
                        </div>

                        <div className="overflow-y-auto max-h-[28rem]">
                            <table className="w-full">
                                <thead className="bg-gray-50 sticky top-0">
                                    <tr className="text-xs text-gray-500 font-medium">
                                        <th className="px-3 py-2 text-left">Name</th>
                                        <th className="px-3 py-2 text-center w-14">Hcp</th>
                                        <th className="px-3 py-2 text-left hidden md:table-cell">Category</th>
                                        <th className="px-2 py-2 text-left hidden sm:table-cell w-16">Skills</th>
                                        <th className="px-2 py-2 text-center hidden sm:table-cell w-14">Fit</th>
                                        <th className="px-2 py-2 w-8"></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {golfers.map(golfer => (
                                        <PlayerRow
                                            key={golfer.id}
                                            golfer={golfer}
                                            selected={golfer.id === selectedGolferId && editingGolfer}
                                            onSelect={() => handleSelectGolfer(golfer.id)}
                                            onRemove={() => handleRemoveGolfer(golfer.id)}
                                            canRemove={golfers.length > 2}
                                            courseFit={courseFitMap[golfer.id] || 0}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Right panel: Course summary OR Golfer editor */}
                <div className="lg:col-span-1 order-1 lg:order-2">
                    {editingGolfer && selectedGolfer ? (
                        <div>
                            <button
                                onClick={handleCloseEditor}
                                className="text-xs text-gray-500 hover:text-gray-700 mb-2 flex items-center gap-1"
                            >
                                ← Back to Course
                            </button>
                            <GolferProfileEditor
                                golfer={selectedGolfer}
                                onChange={handleGolferUpdate}
                            />
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="bg-white border border-gray-200 rounded-lg p-4">
                                <h3 className="font-semibold text-gray-800 text-sm mb-2">Course</h3>
                                <CourseSelector
                                    selectedCourse={course}
                                    onCourseChange={setCourse}
                                />
                            </div>
                            <CourseSummaryCard
                                course={course}
                                golfers={golfers}
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* Section 3: Simulation controls + results (full width) */}
            <div className="mt-4 space-y-4">
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-800 mb-3 text-sm">Run Simulation</h3>

                    <div className="flex flex-wrap items-center gap-3">
                        <button
                            onClick={handleSimulateSingle}
                            disabled={isSimulating || golfers.length < 2}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                        >
                            {isSimulating && simProgress.total <= 1 ? 'Simulating...' : 'Simulate Tournament'}
                        </button>

                        <button
                            onClick={() => handleSimulateMultiple(100)}
                            disabled={isSimulating || golfers.length < 2}
                            className="px-4 py-2 border border-green-600 text-green-600 rounded-lg hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                        >
                            100 Simulations
                        </button>

                        <button
                            onClick={() => handleSimulateMultiple(500)}
                            disabled={isSimulating || golfers.length < 2}
                            className="px-4 py-2 border border-green-600 text-green-600 rounded-lg hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                        >
                            500 Simulations
                        </button>

                        {isSimulating && simProgress.total > 1 && (
                            <span className="text-sm text-gray-500 font-mono">
                                {simProgress.done} / {simProgress.total}
                            </span>
                        )}
                    </div>

                    {/* Progress bar */}
                    {isSimulating && simProgress.total > 1 && (
                        <div className="mt-3 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-green-500 rounded-full transition-all duration-150"
                                style={{ width: `${(simProgress.done / simProgress.total) * 100}%` }}
                            />
                        </div>
                    )}

                    {golfers.length < 2 && (
                        <p className="text-sm text-amber-600 mt-2">
                            Add at least 2 golfers to run a tournament
                        </p>
                    )}
                </div>

                {/* Results */}
                {singleResult && (
                    <div className="space-y-4">
                        <H2Heading>Tournament Results</H2Heading>
                        <TournamentStats result={singleResult} />
                        <LeaderboardView
                            standings={singleResult.standings}
                            tournament={singleResult.tournament}
                            course={singleResult.course}
                            cutInfo={singleResult.cutInfo}
                        />
                    </div>
                )}

                {multiResults && (
                    <div className="space-y-4">
                        <H2Heading>Simulation Results ({multiResults.simulations} tournaments)</H2Heading>
                        <SimulationResultsTable stats={multiResults} />
                    </div>
                )}
            </div>

            {/* Methodology note */}
            <div className="mt-8 p-4 bg-gray-50 rounded-lg text-sm text-gray-600">
                <strong>How It Works:</strong> Each golfer has a handicap (overall ability) plus
                skill modifiers in four areas: Off the Tee, Approach, Around the Green, and Putting.
                These modifiers shift scoring probabilities based on hole type - bombers do better
                on par 5s, short game wizards recover better from missed greens.
                Course characteristics determine which skills matter most.{' '}
                <Link
                    to="/sports-models/golf/methodology"
                    className="text-green-600 hover:text-green-800 underline"
                >
                    Learn more about the methodology →
                </Link>
            </div>
        </PageBody>
    );
};

export default GolfProfiles;
